import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Pencil, Trash2, Ban, Pause, Play, FileSignature, HardHat,
  CreditCard, Package, Eye, EyeOff, Mail, Calculator,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SortableTh } from "@/components/TableControls";
import TimeDeltaBadge from "@/components/TimeDeltaBadge";
import ProfitabilityBadge from "@/components/ProfitabilityBadge";
import { OrderQRCodeButton } from "@/components/orders/OrderQRCodeDialog";
import { DisputeBadge } from "@/components/admin/DisputeBadge";
import { CanAction } from "@/lib/rbac";

/**
 * Table des commandes — extrait de OrdersPage pour limiter la taille du parent.
 * Gère le filtrage par profit/paiements + tri par colonne + masquage dynamique.
 */
export const OrdersTable = ({
  // Data
  orders, filters, setFilters,
  profitability, timeDeltas, materialMargins, orderTypes,
  orderProgressCumulatives = {},
  statusLabels, statusColors, formatCurrency,
  // Columns + tri
  visibleOrderColumns, isOrderColVisible, orderSortArrow, handleOrderSort, sortOrders,
  // Actions
  handleToggleDeposit, handleTogglePaid, openPaymentsDialog, openHoldDialog,
  setCompatibleProductsOrder, openDialog, handleCancelOrder, handleToggleHidden,
  handleDelete, setWorkSessionsOrder, setPvOrder, setReminderDialog, setProgressDialogOrder,
  handleMarkCashed,
  // Selection (bulk actions)
  selectedOrderIds = [], toggleOrderSelection, toggleSelectAllFiltered,
  // Phase 177 — logistics badges
  logisticsSummary = {},
}) => {
  const nowMs = Date.now();
  const navigate = useNavigate();

  // Navigation au clic ligne (sauf sur boutons / inputs / éléments interactifs)
  const handleRowClick = (orderId) => (e) => {
    const tag = e.target.tagName;
    if (tag === "BUTTON" || tag === "INPUT" || tag === "A" || tag === "SELECT" || tag === "TEXTAREA") return;
    if (e.target.closest("button, a, input, select, textarea, [data-no-row-nav]")) return;
    navigate(`/commandes/${orderId}`);
  };

  // Helpers : un filtre array vide = aucun filtre actif (tout passe)
  const matchStatus = (o) => {
    const arr = filters.status || [];
    if (!arr.length) return true;
    // Cas spécial "paused" : la commande doit avoir is_on_hold=true
    // (le vrai status sous-jacent peut être n'importe lequel).
    if (arr.includes("paused") && o.is_on_hold) return true;
    // Pour les autres statuts, on exclut les commandes en pause sauf si "paused" est dans le filtre.
    if (o.is_on_hold && !arr.includes("paused")) return false;
    return arr.includes(o.status);
  };

  const matchSeller = (o) => {
    const arr = filters.seller_id || [];
    if (!arr.length) return true;
    return arr.includes(o.seller_id);
  };

  const matchWorker = (o) => {
    const arr = filters.worker || [];
    if (!arr.length) return true;
    // Chaque valeur de la liste est de la forme "emp-{id}" ou "sub-{id}"
    const oEmp = (o.worker_employees || []).map((x) => `emp-${x.id || x}`);
    const oSub = (o.worker_subcontractors || []).map((x) => `sub-${x.id || x}`);
    const single = o.worker_employee_id ? [`emp-${o.worker_employee_id}`] : [];
    const singleSub = o.worker_subcontractor_id ? [`sub-${o.worker_subcontractor_id}`] : [];
    const workersOfOrder = new Set([...oEmp, ...oSub, ...single, ...singleSub]);
    return arr.some((v) => workersOfOrder.has(v));
  };

  const matchProfit = (o) => {
    const arr = filters.profit || [];
    if (!arr.length) return true;
    const p = profitability[o.id];
    return arr.some((tag) => {
      if (tag === "profitable") return p && ["above_target", "near_target"].includes(p.status);
      if (tag === "below") return p && p.status === "below_target";
      if (tag === "negative") return p && p.status === "negative";
      if (tag === "no_data") return !p || p.status === "no_data";
      return false;
    });
  };

  const matchPayment = (o) => {
    const arr = filters.payment || [];
    if (!arr.length) return true;
    const total = o.amount_ttc ?? (o.amount_ht || 0) * (1 + (o.vat_rate ?? 20) / 100);
    const paid = o.total_paid || 0;
    const pct = total > 0 ? (paid / total) * 100 : 0;
    return arr.some((tag) => {
      if (tag === "unpaid") return paid <= 0;
      if (tag === "partial") return paid > 0 && pct < 100;
      if (tag === "paid") return pct >= 100 && total > 0;
      if (tag === "overdue") {
        if (pct >= 100) return false;
        const ref = o.completed_at || o.created_at;
        if (!ref) return false;
        const days = (nowMs - new Date(ref).getTime()) / (1000 * 60 * 60 * 24);
        return days > 30;
      }
      return false;
    });
  };

  const matchProgress = (o) => {
    const arr = filters.progress || [];
    if (!arr.length) return true;
    const pct = orderProgressCumulatives[o.id]?.total_percent || 0;
    return arr.some((tag) => {
      if (tag === "empty") return pct <= 0;
      if (tag === "partial") return pct > 0 && pct < 50;
      if (tag === "advanced") return pct >= 50 && pct < 100;
      if (tag === "complete") return pct === 100;
      if (tag === "over") return pct > 100;
      return false;
    });
  };

  const matchMoneyOut = (o) => {
    if (!filters.money_out) return true;
    // Reproduit la règle backend `utils/outstanding.money_out` :
    //  - cancelled / cashed / hidden / imported / on_hold : exclus
    //  - completed : solde dû > 0
    //  - autre statut : acompte attendu > 0 (deposit_paid = false)
    if (["cancelled", "cashed"].includes(o.status)) return false;
    if (o.hidden || o.is_imported || o.is_on_hold) return false;
    const ttc = o.amount_ttc ?? (o.amount_ht || 0) * (1 + (o.vat_rate ?? 20) / 100);
    const paid = o.total_paid || 0;
    if (o.status === "completed") return ttc - paid > 0.01;
    if (!o.deposit_paid) {
      const expected = o.deposit_amount ?? ttc * 0.4;
      return expected - paid > 0.01;
    }
    return false;
  };

  const filtered = orders.filter(
    (o) => matchStatus(o) && matchSeller(o) && matchWorker(o) && matchProfit(o) && matchPayment(o) && matchProgress(o) && matchMoneyOut(o)
  );

  const sortAccessors = {
    amount_ttc: (o) => o.amount_ttc ?? (o.amount_ht || 0) * (1 + (o.vat_rate ?? 20) / 100),
    status: (o) => statusLabels[o.status] || o.status,
  };
  const sorted = sortOrders(filtered, sortAccessors);
  const activeFilterCount =
    (filters.status?.length || 0) +
    (filters.seller_id?.length || 0) +
    (filters.worker?.length || 0) +
    (filters.profit?.length || 0) +
    (filters.payment?.length || 0) +
    (filters.progress?.length || 0);
  const showBanner = activeFilterCount > 0;

  // Dispatcher de rendu : chaque clé de colonne → une fonction qui renvoie le <td> pour une commande donnée.
  // Permet l'alignement automatique thead/tbody lorsque l'utilisateur réordonne ou masque des colonnes.
  const renderCell = (colKey, order, ctx) => {
    switch (colKey) {
      case 'reference': return (
        <td key={colKey} className="font-mono text-sm">
          <span className="inline-flex items-center gap-1">
            {order.reference}
            <DisputeBadge orderId={order.id} size={12} />
          </span>
        </td>
      );
      case 'order_date': return (
        <td key={colKey} className="text-sm whitespace-nowrap">
          {order.order_date
            ? new Date(order.order_date).toLocaleDateString("fr-FR")
            : <span className="text-muted-foreground">—</span>}
        </td>
      );
      case 'work_description': return (
        <td key={colKey} className="text-sm max-w-[260px]">
          {order.work_description
            ? <span className="truncate block" title={order.work_description}>{order.work_description}</span>
            : <span className="text-muted-foreground">—</span>}
        </td>
      );
      case 'client_name': return <td key={colKey} className="font-medium">{order.client_name}</td>;
      case 'city': return <td key={colKey}>{order.city || "-"}</td>;
      case 'amount_ht': return <td key={colKey} className="currency">{formatCurrency(order.amount_ht)}</td>;
      case 'vat_rate': return <td key={colKey} className="text-xs text-muted-foreground tabular-nums">{(order.vat_rate ?? 20)}%</td>;
      case 'amount_ttc': return (
        <td key={colKey} className="currency font-medium" data-testid={`amount-ttc-${order.id}`}>
          {formatCurrency(order.amount_ttc ?? (order.amount_ht || 0) * (1 + (order.vat_rate ?? 20) / 100))}
        </td>
      );
      case 'order_type_name': return <td key={colKey}>{order.order_type_name}</td>;
      case 'seller_name': return <td key={colKey}>{order.seller_name || "-"}</td>;
      case 'estimated_duration': return (
        <td key={colKey}>
          <div className="flex flex-col gap-1">
            <span className="text-sm">{order.estimated_duration} j</span>
            {timeDeltas[order.id] && <TimeDeltaBadge delta={timeDeltas[order.id]} compact />}
            {profitability[order.id] && profitability[order.id].status !== "no_data" && (
              <ProfitabilityBadge data={profitability[order.id]} compact />
            )}
          </div>
        </td>
      );
      case 'status': return (
        <td key={colKey}>
          <div className="flex items-center gap-2 flex-wrap">
            {order.is_on_hold ? (
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800" data-testid={`status-badge-paused-${order.id}`}>
                En pause
              </Badge>
            ) : (
              <Badge className={statusColors[order.status]} data-testid={`status-badge-${order.id}`}>
                {statusLabels[order.status]}
              </Badge>
            )}
            {/* Logistics badges (Phase 177) */}
            {(() => {
              const summary = logisticsSummary[order.id];
              if (!summary || !summary.by_category?.length) return null;
              return summary.by_category.map((b) => {
                const cls = b.status_color === "green"
                  ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                  : b.status_color === "amber"
                    ? "bg-amber-100 text-amber-700 border border-amber-200"
                    : "bg-red-100 text-red-700 border border-red-200";
                return (
                  <span
                    key={b.category_id}
                    className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 font-medium ${cls}`}
                    style={b.color ? { borderLeftWidth: 3, borderLeftColor: b.color } : undefined}
                    title={b.est_interne_administratif ? `${b.name} (interne)` : b.name}
                    data-testid={`logistics-badge-${order.id}-${b.category_id}`}
                  >
                    {b.name} {b.delivered}/{b.total}
                  </span>
                );
              });
            })()}
            {!order.is_on_hold && order.status === "completed" && handleMarkCashed && (
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-2 text-xs border-green-500 text-green-800 hover:bg-green-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkCashed(order);
                }}
                data-testid={`mark-cashed-${order.id}`}
                title="Marquer la commande comme Encaissée (déclenche la commission vendeur)"
              >
                Encaisser
              </Button>
            )}
          </div>
        </td>
      );
      case 'payment': return (
        <td key={colKey}>
          <div className="space-y-1 min-w-[180px]" data-testid={`payment-cell-${order.id}`}>
            {(() => {
              const total = order.amount_ttc ?? (order.amount_ht || 0) * (1 + (order.vat_rate ?? 20) / 100);
              const paid = order.total_paid || 0;
              const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
              const remaining = Math.max(total - paid, 0);
              const fullyPaid = paid >= total && total > 0;
              const barColor = fullyPaid ? "bg-green-500" : pct >= 40 ? "bg-amber-500" : "bg-slate-400";
              return (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium tabular-nums">
                      {formatCurrency(paid)} <span className="text-muted-foreground">/ {formatCurrency(total)}</span>
                    </span>
                    <span className={`font-semibold tabular-nums ${fullyPaid ? "text-green-600" : "text-muted-foreground"}`}>
                      {pct}%
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden" title={`${pct}% payé`}>
                    <div
                      className={`h-full ${barColor} transition-all`}
                      style={{ width: `${pct}%` }}
                      data-testid={`payment-progress-${order.id}`}
                    />
                  </div>
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    <Badge
                      variant={order.deposit_paid ? "default" : "outline"}
                      className="text-[10px] px-1.5 py-0 h-5 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleToggleDeposit(order.id)}
                      data-testid={`toggle-deposit-${order.id}`}
                    >
                      Acompte {order.deposit_paid ? "✓" : "✗"}
                    </Badge>
                    <Badge
                      variant={order.order_paid ? "default" : "outline"}
                      className="text-[10px] px-1.5 py-0 h-5 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleTogglePaid(order.id)}
                      data-testid={`toggle-paid-${order.id}`}
                    >
                      Solde {order.order_paid ? "✓" : "✗"}
                    </Badge>
                    {!fullyPaid && remaining > 0 && paid > 0 && (
                      <span className="text-[10px] text-muted-foreground self-center">
                        Reste : {formatCurrency(remaining)}
                      </span>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </td>
      );
      case 'progress': {
        const cum = orderProgressCumulatives[order.id] || { total_percent: 0, total_ca: 0 };
        const pct = cum.total_percent || 0;
        const clampedPct = Math.min(100, Math.max(0, pct));
        const over100 = pct > 100;
        const barColor = over100
          ? "bg-orange-500"
          : pct >= 100
          ? "bg-green-500"
          : pct >= 50
          ? "bg-teal-500"
          : "bg-teal-400";
        return (
          <td key={colKey}>
            <div className="flex flex-col gap-1 min-w-[120px]">
              <div className="flex items-center justify-between text-xs">
                <span className={`font-semibold tabular-nums ${over100 ? "text-orange-600" : pct === 0 ? "text-muted-foreground" : "text-teal-700"}`}>
                  {pct.toFixed(0)}%
                </span>
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  / 100%
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden" title={`${pct.toFixed(1)}% cumulé — CA ${formatCurrency(cum.total_ca)}`}>
                <div
                  className={`h-full ${barColor} transition-all`}
                  style={{ width: `${clampedPct}%` }}
                  data-testid={`progress-bar-${order.id}`}
                />
              </div>
              {pct > 0 && (
                <div className="text-[10px] text-muted-foreground currency tabular-nums">
                  {formatCurrency(cum.total_ca)}
                </div>
              )}
            </div>
          </td>
        );
      }
      case 'actions': return (
        <td key={colKey}>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={() => setWorkSessionsOrder(order)} data-testid={`work-sessions-${order.id}`} title="Jours et heures travaillées">
              <HardHat className="h-4 w-4 text-indigo-600" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setPvOrder(order)} data-testid={`pv-order-${order.id}`}
              title={
                order.pv_signed
                  ? order.pv_has_reserves
                    ? (order.pv_reserves_cleared_date ? "PV signé — réserves levées" : "PV signé — avec réserves")
                    : "PV signé sans réserves"
                  : "PV de réception"
              }
            >
              <FileSignature
                className={`h-4 w-4 ${
                  order.pv_signed
                    ? order.pv_has_reserves && !order.pv_reserves_cleared_date
                      ? "text-amber-500"
                      : "text-green-600"
                    : "text-slate-400"
                }`}
              />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => openPaymentsDialog(order)} data-testid={`payments-order-${order.id}`} title="Gérer les paiements">
              <CreditCard className="h-4 w-4 text-blue-500" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => openHoldDialog(order)} data-testid={`hold-order-${order.id}`} title={order.is_on_hold ? "Réactiver la commande" : "Mettre en pause"}>
              {order.is_on_hold ? <Play className="h-4 w-4 text-green-500" /> : <Pause className="h-4 w-4 text-yellow-500" />}
            </Button>
            <Button variant="ghost" size="icon"
              onClick={() => setCompatibleProductsOrder({
                ...order,
                order_type_name: orderTypes.find(t => t.id === order.order_type_id)?.name,
              })}
              data-testid={`compatible-products-${order.id}`}
              title={materialMargins[order.id]
                ? `Matière verrouillée : ${materialMargins[order.id].materials_cost?.toFixed(2)} € (marge ${materialMargins[order.id].material_margin_pct?.toFixed(1)}%)`
                : "Produits compatibles & estimation matière"}
            >
              <Package className={`h-4 w-4 ${materialMargins[order.id] ? "text-green-600" : "text-indigo-600"}`} />
              {materialMargins[order.id]?.material_margin_pct != null && (
                <span className={`ml-0.5 text-[9px] font-mono font-bold ${
                  materialMargins[order.id].material_margin_pct < 20 ? "text-red-600" :
                  materialMargins[order.id].material_margin_pct < 40 ? "text-amber-600" : "text-green-600"
                }`}>
                  {materialMargins[order.id].material_margin_pct.toFixed(0)}%
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => openDialog(order)} data-testid={`edit-order-${order.id}`} title="Modifier">
              <Pencil className="h-4 w-4" />
            </Button>
            <OrderQRCodeButton order={order} size="icon" />
            {setProgressDialogOrder && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setProgressDialogOrder(order)}
                data-testid={`progress-order-${order.id}`}
                title="Avancement CA mensuel"
              >
                <Calculator className="h-4 w-4 text-teal-600" />
              </Button>
            )}
            {order.status !== "cancelled" && (
              <Button variant="ghost" size="icon" onClick={() => handleCancelOrder(order.id)} data-testid={`cancel-order-${order.id}`} title="Annuler la commande">
                <Ban className="h-4 w-4 text-orange-500" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => handleToggleHidden(order)} data-testid={`toggle-hidden-${order.id}`} title={order.hidden ? "Réafficher" : "Masquer"}>
              {order.hidden ? <Eye className="h-4 w-4 text-slate-500" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
            </Button>
            {ctx.missingDeposit && (
              <Button variant="ghost" size="icon"
                onClick={() => setReminderDialog({ open: true, order, email: order.client_email || "", message: "", sending: false })}
                data-testid={`send-reminder-${order.id}`}
                title="Relancer l'acompte par email"
              >
                <Mail className="h-4 w-4 text-red-600" />
              </Button>
            )}
            <CanAction action="delete_orders" fallback={
              <Button variant="ghost" size="icon" disabled title="Suppression non autorisée" data-testid={`delete-order-${order.id}-disabled`}>
                <Trash2 className="h-4 w-4 opacity-30" />
              </Button>
            }>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(order.id)} data-testid={`delete-order-${order.id}`} title="Supprimer">
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </CanAction>
          </div>
        </td>
      );
      default: return null;
    }
  };

  return (
    <>
      {showBanner && (
        <div className="px-4 py-2 text-xs text-muted-foreground border-b flex items-center justify-between">
          <span data-testid="payment-filter-count">
            {filtered.length} commande(s) affichée(s) sur {orders.length}
          </span>
          <button
            className="text-primary underline-offset-4 hover:underline"
            onClick={() => setFilters({ ...filters, status: [], seller_id: [], worker: [], payment: [], profit: [], progress: [] })}
            data-testid="clear-payment-filter"
          >
            Réinitialiser
          </button>
        </div>
      )}
      <table className="data-table" x-excluded="true">
        <thead x-excluded="true">
          <tr x-excluded="true">
            {toggleOrderSelection && (
              <th className="w-10 px-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer"
                  checked={sorted.length > 0 && sorted.every((o) => selectedOrderIds.includes(o.id))}
                  onChange={() => toggleSelectAllFiltered(sorted.map((o) => o.id))}
                  data-testid="order-select-all"
                  aria-label="Tout sélectionner"
                />
              </th>
            )}
            {visibleOrderColumns.map((col) => (
              <SortableTh
                key={col.key}
                columnKey={col.key}
                label={col.label}
                sortable={col.sortable !== false}
                sortArrow={orderSortArrow}
                handleSort={handleOrderSort}
              />
            ))}
          </tr>
        </thead>
        <tbody x-excluded="true">
          {sorted.map((order) => {
            const missingDeposit = !order.deposit_paid
              && order.status !== "cancelled"
              && !order.is_on_hold;
            const isSelected = selectedOrderIds.includes(order.id);
            return (
              <tr
                key={order.id}
                data-testid={`order-row-${order.id}`}
                x-excluded="true"
                onClick={handleRowClick(order.id)}
                className={`cursor-pointer transition-colors hover:bg-blue-50/40 ${
                  isSelected ? "bg-indigo-50/60"
                  : order.hidden ? "opacity-50 bg-slate-50"
                  : missingDeposit ? "bg-red-50 border-l-4 border-l-red-500"
                  : ""
                }`}
              >
                {toggleOrderSelection && (
                  <td className="w-10 px-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer"
                      checked={isSelected}
                      onChange={() => toggleOrderSelection(order.id)}
                      data-testid={`order-select-${order.id}`}
                      aria-label={`Sélectionner ${order.reference}`}
                    />
                  </td>
                )}
                {visibleOrderColumns.map((col) => renderCell(col.key, order, { missingDeposit }))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export default OrdersTable;
